/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, Loader2, CheckCircle2,
  ClipboardList, Beaker, ArrowRight, ShieldCheck, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const STATUS_TONE = {
  draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  in_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  blocked_pending_verify: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  archived: 'bg-white/10 text-white/40 border-white/10',
};

const BLOCKER_LABEL = {
  unverified_inventory_rows_present: 'Unverified inventory rows remain',
  no_protocol_items_on_phase: 'No protocol items found on this phase',
  phase_not_yet_started: 'Phase has not started execution yet',
  open_delivery_issue: 'A delivery issue is open and awaiting review',
};

const WARNING_LABEL = {
  low_feedback_signal_count: 'Low feedback signal count',
  high_skip_rate: 'High skip rate (>30%)',
};

export default function FormulationSnapshot() {
  const { subscriberId, phaseId } = useParams();
  const { user } = useAuth();
  const canWrite = user?.role === 'admin' || user?.role === 'counselor';

  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [moving, setMoving] = useState(false);

  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmNote, setConfirmNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(
        `${API}/formulation/snapshot/${subscriberId}/${phaseId}`,
      );
      setSnap(r.data.snapshot);
    } catch (e) {
      if (e?.response?.status === 404) {
        setSnap(null);
      } else {
        setPageError(e?.response?.data?.detail || 'Could not load snapshot');
      }
    } finally {
      setLoading(false);
    }
  }, [subscriberId, phaseId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/formulation/snapshot/${subscriberId}/${phaseId}/generate`,
      );
      setSnap(r.data.snapshot);
      toast.success('Snapshot generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  const moveStatus = async (toStatus) => {
    if (!snap) return;
    setMoving(true);
    try {
      const r = await axios.patch(
        `${API}/formulation/snapshot/${snap.id}/status`,
        { to_status: toStatus, note: confirmNote || undefined },
      );
      setSnap(r.data.snapshot);
      toast.success(`Snapshot moved to ${toStatus}`);
      setConfirmOpen(false);
      setConfirmNote('');
    } catch (e) {
      const data = e?.response?.data;
      const blockers = data?.detail?.blockers;
      if (blockers && blockers.length > 0) {
        toast.error(`Blocked: ${blockers.map((b) => BLOCKER_LABEL[b] || b).join(', ')}`);
      } else {
        toast.error(typeof data?.detail === 'string' ? data.detail : 'Status change failed');
      }
    } finally {
      setMoving(false);
    }
  };

  const addNote = async () => {
    if (!snap || !noteText.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(
        `${API}/formulation/snapshot/${snap.id}/note`,
        { text: noteText.trim() },
      );
      setSnap(r.data.snapshot);
      setNoteText('');
      toast.success('Note added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Note failed');
    } finally {
      setPosting(false);
    }
  };

  const askConfirm = (target) => {
    setConfirmTarget(target);
    setConfirmNote('');
    setConfirmOpen(true);
  };

  if (!user) return null;
  const readiness = snap?.report_readiness;

  return (
    <AdminLayout>
      <div
        data-testid="formulation-snapshot-page"
        className="px-6 py-6 max-w-6xl mx-auto"
      >
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <FileText className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">17-Day Phase Snapshot</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">F4</Badge>
            {snap && (
              <span
                data-testid="snapshot-status-pill"
                className={`px-2 py-0.5 rounded border text-[10px] ${STATUS_TONE[snap.status] || ''}`}
              >
                {snap.status}
              </span>
            )}
          </div>
          <p className="text-sm text-white/50">
            Operational aggregation of adherence, inventory, and feedback. No biological inference.
          </p>
          <p className="text-xs text-white/40 mt-1">
            <span className="font-mono">subscriber:</span> {subscriberId}
            {' · '}
            <span className="font-mono">phase:</span> {phaseId}
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {canWrite && (
            <Button
              data-testid="snapshot-generate-btn"
              onClick={generate}
              disabled={generating}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-9 text-xs"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              {snap ? 'Refresh snapshot' : 'Generate snapshot'}
            </Button>
          )}
          <Link
            to={`/admin/formulation/inventory/${subscriberId}/${phaseId}`}
            data-testid="snapshot-open-inventory-link"
          >
            <Button
              variant="outline"
              size="sm"
              className="border-white/15 text-white/70 h-9 text-xs"
            >
              <Beaker className="w-3.5 h-3.5 mr-1" /> Inventory
            </Button>
          </Link>
          <Link
            to={`/admin/formulation/feedback/${subscriberId}/${phaseId}`}
            data-testid="snapshot-open-feedback-link"
          >
            <Button
              variant="outline"
              size="sm"
              className="border-white/15 text-white/70 h-9 text-xs"
            >
              <ClipboardList className="w-3.5 h-3.5 mr-1" /> Feedback log
            </Button>
          </Link>
          <Link
            to={`/admin/formulation/production/${subscriberId}/${phaseId}`}
            data-testid="snapshot-open-production-link"
          >
            <Button
              variant="outline"
              size="sm"
              className="border-white/15 text-white/70 h-9 text-xs"
            >
              Production
            </Button>
          </Link>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}

        {pageError && !loading && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
               data-testid="snapshot-page-error">
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
          </div>
        )}

        {!loading && !snap && !pageError && (
          <div
            data-testid="snapshot-empty-state"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-8 text-center"
          >
            <FileText className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <div className="text-sm text-white/70">No snapshot yet for this phase.</div>
            {canWrite && (
              <div className="text-xs text-white/50 mt-1">
                Click <span className="text-cyan-300">Generate snapshot</span> to create one.
              </div>
            )}
          </div>
        )}

        {snap && (
          <>
            {/* Readiness */}
            <ReadinessPanel readiness={readiness} />

            {/* Three KPI cards */}
            <div className="grid md:grid-cols-3 gap-3 mb-3">
              <AdherenceCard adherence={snap.adherence} />
              <InventoryCard inventory={snap.inventory} />
              <FeedbackCard feedback={snap.feedback} />
            </div>

            {/* Production strip */}
            <ProductionStrip production={snap.production} />

            {/* Status controls */}
            {canWrite && (
              <StatusControls
                snap={snap}
                readiness={readiness}
                disabled={moving}
                onMove={(target) => askConfirm(target)}
              />
            )}

            {/* Notes thread */}
            <NotesThread
              snap={snap}
              canWrite={canWrite}
              noteText={noteText}
              onChange={setNoteText}
              onSubmit={addNote}
              posting={posting}
            />

            {/* Review history */}
            <ReviewHistory history={snap.review_history || []} />
          </>
        )}
      </div>

      {/* Confirm transition modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="snapshot-status-confirm-modal"
          className="bg-slate-900 border border-white/10"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              Move snapshot to <span className="text-cyan-300 font-mono">{confirmTarget}</span>?
            </DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              {confirmTarget === 'approved'
                ? 'Approval is final and is recorded in the audit trail. The server will recheck readiness before approving.'
                : 'This is recorded as a status-change event in the snapshot history.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[11px] text-white/60">Note (optional)</Label>
            <Textarea
              data-testid="snapshot-status-confirm-note-input"
              rows={3}
              maxLength={1000}
              value={confirmNote}
              onChange={(e) => setConfirmNote(e.target.value)}
              className="bg-slate-950/40 border-white/10 text-sm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              className="border-white/15 text-white/80"
            >
              Cancel
            </Button>
            <Button
              data-testid="snapshot-status-confirm-btn"
              onClick={() => moveStatus(confirmTarget)}
              disabled={moving}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30"
            >
              {moving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function ReadinessPanel({ readiness }) {
  if (!readiness) return null;
  const ready = !!readiness.ready;
  return (
    <div
      data-testid="snapshot-readiness-panel"
      className={`rounded-lg border p-3 mb-3 ${
        ready
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-amber-500/30 bg-amber-500/5'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {ready ? (
          <ShieldCheck className="w-4 h-4 text-emerald-300" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-amber-300" />
        )}
        <span
          data-testid="snapshot-readiness-flag"
          className={`text-xs font-medium ${ready ? 'text-emerald-300' : 'text-amber-300'}`}
        >
          {ready ? 'Ready for approval' : 'Approval blocked'}
        </span>
      </div>
      {(readiness.blockers || []).length > 0 && (
        <ul className="text-xs space-y-1 mb-2">
          {readiness.blockers.map((b) => (
            <li
              key={b}
              data-testid={`snapshot-readiness-blocker-${b}`}
              className="text-amber-200 flex items-center gap-2"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
              {BLOCKER_LABEL[b] || b}
            </li>
          ))}
        </ul>
      )}
      {(readiness.warnings || []).length > 0 && (
        <ul className="text-xs space-y-1 text-white/50">
          {readiness.warnings.map((w) => (
            <li
              key={w}
              data-testid={`snapshot-readiness-warning-${w}`}
              className="flex items-center gap-2"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
              <span className="text-white/60">Warning:</span> {WARNING_LABEL[w] || w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AdherenceCard({ adherence }) {
  return (
    <div
      data-testid="snapshot-adherence-card"
      className="rounded-lg border border-white/10 bg-slate-900/40 p-3"
    >
      <div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Adherence</div>
      <div className="text-3xl font-semibold text-white">
        {adherence.adherence_percentage}%
      </div>
      <div className="text-xs text-white/50 mt-2 space-y-0.5">
        <div>Executed: <span className="text-emerald-300">{adherence.executed_count}</span> / {adherence.expected_doses}</div>
        <div>Skipped: <span className="text-amber-300">{adherence.skipped_count}</span></div>
        <div>Paused: <span className="text-slate-300">{adherence.paused_count}</span></div>
        <div className="text-white/40">Phase length: {adherence.phase_length_days}d</div>
      </div>
    </div>
  );
}

function InventoryCard({ inventory }) {
  return (
    <Link
      to="#inventory-rollup"
      data-testid="snapshot-inventory-card"
      className="rounded-lg border border-white/10 bg-slate-900/40 p-3 block hover:border-cyan-500/30 transition-colors"
    >
      <div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Inventory</div>
      <div className="text-3xl font-semibold text-white">{inventory.total_items}</div>
      <div className="text-xs text-white/50 mt-2 grid grid-cols-2 gap-y-0.5 gap-x-2">
        <div>Verified: <span className="text-emerald-300">{inventory.verified}</span></div>
        <div>Unverified: <span className="text-slate-300">{inventory.unverified}</span></div>
        <div>Mismatch: <span className="text-fuchsia-300">{inventory.mismatch}</span></div>
        <div>Low: <span className="text-amber-300">{inventory.low}</span></div>
      </div>
    </Link>
  );
}

function FeedbackCard({ feedback }) {
  const types = Object.entries(feedback.by_issue_type || {}).slice(0, 4);
  return (
    <div
      data-testid="snapshot-feedback-card"
      className="rounded-lg border border-white/10 bg-slate-900/40 p-3"
    >
      <div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Feedback signals</div>
      <div className="text-3xl font-semibold text-white">{feedback.total}</div>
      <div className="text-xs text-white/50 mt-2 space-y-0.5">
        {types.length === 0 ? (
          <div className="text-white/40">No feedback captured yet.</div>
        ) : types.map(([k, v]) => (
          <div key={k} className="flex justify-between">
            <span className="capitalize">{k.replace(/_/g, ' ')}</span>
            <span className="text-white/70">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductionStrip({ production }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-3 flex flex-wrap gap-4 items-center text-xs">
      <span className="text-white/40">production:</span>
      <span className="font-mono text-cyan-200">{production.current_status || '—'}</span>
      <span><span className="text-white/40">delivered:</span> {production.delivered_on || '—'}</span>
      <span><span className="text-white/40">exec_start:</span> {production.execution_start_date || '—'}</span>
      {production.subscriber_confirmed_delivery && (
        <span className="px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
          Subscriber confirmed
        </span>
      )}
      {production.delivery_issue_open && (
        <span className="px-2 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">
          Delivery issue open
        </span>
      )}
    </div>
  );
}

function StatusControls({ snap, readiness, disabled, onMove }) {
  const status = snap.status;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 mb-3 flex flex-wrap items-center gap-2">
      <span className="text-xs text-white/50 mr-2">Lifecycle:</span>
      {(status === 'draft' || status === 'blocked_pending_verify') && (
        <Button
          data-testid="snapshot-status-move-review-btn"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onMove('in_review')}
          className="border-white/15 text-white/80 h-7 text-[11px]"
        >
          Move to In review
        </Button>
      )}
      {status === 'in_review' && (
        <Button
          data-testid="snapshot-status-back-to-draft-btn"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onMove('draft')}
          className="border-white/15 text-white/60 h-7 text-[11px]"
        >
          Back to Draft
        </Button>
      )}
      {(status === 'in_review' || status === 'draft') && (
        <Button
          data-testid="snapshot-status-approve-btn"
          size="sm"
          disabled={disabled || !readiness?.ready}
          title={readiness?.ready ? '' : 'Resolve all unverified inventory rows before approval'}
          onClick={() => onMove('approved')}
          className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 h-7 text-[11px] disabled:opacity-50"
        >
          <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
        </Button>
      )}
      {status !== 'archived' && (
        <Button
          data-testid="snapshot-status-archive-btn"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onMove('archived')}
          className="border-white/15 text-white/40 h-7 text-[11px] ml-auto"
        >
          Archive
        </Button>
      )}
    </div>
  );
}

function NotesThread({ snap, canWrite, noteText, onChange, onSubmit, posting }) {
  const notes = snap.notes || [];
  return (
    <div
      data-testid="snapshot-notes-section"
      className="rounded-lg border border-white/10 bg-slate-900/40 p-3 mb-3"
    >
      <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">Care-team notes</div>
      <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
        {notes.length === 0 && <div className="text-xs text-white/40">No notes yet.</div>}
        {notes.map((n, i) => (
          <div key={i} className="rounded border border-white/5 bg-slate-950/40 p-2 text-xs">
            <div className="text-white/40 text-[10px] mb-1">
              {new Date(n.at).toLocaleString()} · {n.actor}
            </div>
            <div className="text-white/80 whitespace-pre-wrap">{n.text}</div>
          </div>
        ))}
      </div>
      {canWrite && (
        <div className="flex flex-col gap-2">
          <Textarea
            data-testid="snapshot-note-input"
            rows={2}
            maxLength={1000}
            value={noteText}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Add an audit note…"
            className="bg-slate-950/40 border-white/10 text-sm"
          />
          <Button
            data-testid="snapshot-note-submit"
            onClick={onSubmit}
            disabled={posting || !noteText.trim()}
            className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-8 text-xs self-end"
          >
            {posting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Add note
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewHistory({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <div
      data-testid="snapshot-review-history"
      className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-xs"
    >
      <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">History</div>
      <ul className="space-y-1">
        {history.slice(-15).reverse().map((h, i) => (
          <li key={i} className="flex flex-wrap gap-2 items-center text-white/60">
            <span className="text-white/40">{new Date(h.at).toLocaleString()}</span>
            <span className="text-cyan-200 font-mono">{h.kind}</span>
            {h.from_status && h.to_status && (
              <span>
                <span className="text-white/40">{h.from_status}</span>
                <ArrowRight className="w-3 h-3 inline mx-1 text-white/30" />
                <span className="text-white/80">{h.to_status}</span>
              </span>
            )}
            {h.note && <span className="text-white/60 italic">— {h.note}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
